/* eslint-disable react/jsx-props-no-spreading */
import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { useUrlGenerator, useRoutePush } from '@micromag/core/contexts';
import { ScreenTypes, createScreen } from '@micromag/editor';
import { ScreensProvider } from '@micromag/screens';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useParams } from 'wouter';

import useTheme from '../../../hooks/useTheme';
import useThemeUpdate from '../../../hooks/useThemeUpdate';

import MainLayout from '../../layouts/Main';
import ThemeMenubar from '../../menubars/Theme';
import Page from '../../partials/Page';

const propTypes = {
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const ThemesShowPage = ({ className }) => {
    const [pathname] = useLocation();
    const { theme: themeId } = useParams();
    const url = useUrlGenerator();
    const push = useRoutePush();

    const { theme = null } = useTheme(themeId);
    const { id = null, title = null, components: screens = null } = theme || {};
    const { update: updateTheme, updating } = useThemeUpdate(themeId);

    const parent = title !== null ? title : null;
    const pageTitle = <FormattedMessage defaultMessage="Themes" descrition="Page title" />;

    const createScreenFromDefinition = useCallback(
        (definition, onCreated) => {
            const newScreen = createScreen(definition);
            const { components: currentScreens = [] } = theme || {};
            const newValue = {
                ...theme,
                components: [...(currentScreens || []), newScreen],
            };
            if (updateTheme !== null) {
                updateTheme(newValue).then((newTheme) => {
                    onCreated(newTheme);
                });
            }
        },
        [theme, updateTheme, updating],
    );

    const onClickItem = useCallback(
        (screen) => {
            const { id: screenIdAsType } = screen || {};
            // Here the screen you get is with type
            const currentScreen = screens.find((s) => s.type === screenIdAsType);
            if (currentScreen) {
                push('themes.editor.path', { theme: id, path: currentScreen.id });
            } else {
                createScreenFromDefinition(screen, (newTheme) => {
                    const { components: newScreens = [] } = newTheme || {};
                    const newScreen = newScreens.find((s) => s.type === screenIdAsType);
                    if (newScreen) {
                        push('themes.editor.path', { theme: id, path: newScreen.id });
                    } else {
                        push('themes.editor', { theme: id });
                    }
                });
            }
        },
        [id, push, url, screens, createScreenFromDefinition],
    );

    return (
        <MainLayout
            nav={[
                { label: pageTitle, url: url('organisations.themes') },
                { label: parent, url: pathname },
            ]}
        >
            <Page
                title={title}
                menubar={
                    theme !== null ? <ThemeMenubar theme={theme} asLine withoutScreens /> : null
                }
                className={className}
            >
                <ScreensProvider>
                    <ScreenTypes
                        selectedTypes={screens ? screens.map(({ type }) => type) : []}
                        onClickItem={onClickItem}
                    />
                </ScreensProvider>
            </Page>
        </MainLayout>
    );
};

ThemesShowPage.propTypes = propTypes;
ThemesShowPage.defaultProps = defaultProps;

export default ThemesShowPage;
